import { type PropsWithChildren, useRef } from 'react';
import styled from 'styled-components';
import { changeColorAlpha } from '@elfsight/lib-utils';
import { getBrightInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../meta';
import { sanitizeNumericInput, validateAge, yearClamper } from '../helpers';
import { inputAriaLabels } from './constants';

export type ItemAllowYearProps = PropsWithChildren<{
  onClick: (value: boolean) => void;
}>;

export const ItemAllowYear = ({ children, onClick }: ItemAllowYearProps) => {
  const input = useRef(null);

  const {
    submitButtonColor,
    textColor,
    submitButtonText,
    minimumAge,
    textFont,
    shape
  } = useSettings();

  const clickHandler = (e) => {
    e.preventDefault();

    const {
      current: { value }
    } = input;
    onClick(validateAge(minimumAge, value));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.target.value.length > 3) {
      clickHandler(e);
    }
  };

  return (
    <>
      <YearInput
        _textColor={textColor || 'inherit'}
        _fontSize={textFont.fontSize}
        _shape={shape}
        onKeyDown={handleKeyPress}
        type="number"
        inputMode="numeric"
        aria-label={inputAriaLabels.YYYY}
        name="year"
        placeholder="YYYY"
        ref={input}
        maxLength={4}
        onChange={({ target: inputElement }) => {
          inputElement.value = sanitizeNumericInput(
            inputElement.value,
            inputElement.maxLength
          );
          inputElement.value = yearClamper(inputElement.value);
        }}
      />

      {children}

      <SubmitButton
        _submitButtonColor={submitButtonColor}
        _fontSize={textFont.fontSize}
        _shape={shape}
        type="button"
        onClick={clickHandler}
      >
        {submitButtonText}
      </SubmitButton>
    </>
  );
};

const YearInput = styled.input.attrs({
  className: `${meta.prefix}-item-allow-year-input`
})`
  padding: 16px;
  outline: none;
  box-sizing: border-box;
  width: 100%;
  line-height: 18px;
  text-align: center;
  transition: 0.3s ease all;
  font-size: ${({ _fontSize }) => _fontSize}px;
  border: 1px solid
    ${({ _textColor }) =>
      _textColor ? changeColorAlpha(_textColor, -0.6) : 'transparent'};
  color: ${({ _textColor }) => _textColor};
  background-color: ${({ _textColor }) =>
    _textColor ? changeColorAlpha(_textColor, -0.95) : 'transparent'};
  border-radius: ${({ _shape }) => (_shape ? '4px' : '0')};

  &::placeholder {
    color: currentColor;
    opacity: 0.5;
  }

  &:focus {
    background-color: ${({ _textColor }) =>
      _textColor ? changeColorAlpha(_textColor, -1) : 'transparent'};
    border-color: ${({ _textColor }) =>
      _textColor ? changeColorAlpha(_textColor, -0.2) : 'transparent'};
  }
`;

const SubmitButton = styled.button.attrs({
  className: `${meta.prefix}-item-allow-year-submit`
})`
  padding: 12px;
  outline: none;
  background-color: ${({ _submitButtonColor }) =>
    _submitButtonColor || 'transparent'};
  cursor: pointer;
  width: 100%;
  margin-top: 32px;
  font-size: ${({ _fontSize }) => _fontSize}px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  color: ${({ _submitButtonColor }) =>
    _submitButtonColor
      ? getBrightInvertedColor(_submitButtonColor, [
          'rgb(255, 255, 255)',
          'rgb(29, 33, 41)'
        ])
      : 'rgb(255, 255, 255)'};
  border: none;
  transition: 0.3s ease background-color;
  border-radius: ${({ _shape }) => (_shape ? '4px' : '0')};

  &:hover {
    background-color: ${({ _submitButtonColor }) =>
      _submitButtonColor
        ? changeColorAlpha(_submitButtonColor, -0.2)
        : 'transparent'};
  }
`;
